/** Optional core shader warmup. Never spawns gameplay effects or touches their
 * live arrays. Three r183 compileAsync traverses/builds pipelines BEFORE its
 * first await, so each job contains one representative graph in isolation.
 * Colour/size uniforms share these programs with the real effect factories.
 *
 * Pass 3: the list is EVERY material an ability, a status or an indicator
 * can create — the pooled cel shapes (CelShapes, ZoneShapes, StatusShapes,
 * the telegraph decals), the aim indicator's own graphs, the dash ghost —
 * and `prewarmCoverage()` states which pool keys are covered so a test can
 * hold the list to the builders. The founder's "super laggy" was, for the
 * most part, a first-use shader compile mid-fight on a graph this list did
 * not have. */
import * as THREE from 'three/webgpu';
import { burstRing, slashArc, starFlash, hexRing, lineWall, zoneRing, boltBeam, muzzleFlash, smear, shell, cracks, releaseCelShape } from './CelShapes.js';
import { column, vortex, runeTrap, nullDome, nullFloor, ZONE_SHAPE_KEYS } from './ZoneShapes.js';
import { hexShield, iceCrust, hazeRing, STATUS_SHAPE_KEYS } from './StatusShapes.js';
import { teleDisc, teleCone, teleLine, teleRim, teleReticle, TELEGRAPH_SHAPE_KEYS } from '../combat/Telegraphs.js';
import { buildIndicatorMesh, buildTickMesh } from '../combat/AimReticle.js';
import { buildDashGhost } from '../characters/CharacterModels.js';
import { createParticleMaterial, createShardMaterial } from './ParticleUtils.js';
import { DeferredWarmup } from '../performance/DeferredWarmup.js';

function particle(shard, blending) {
  const positions = new THREE.InstancedBufferAttribute(new Float32Array(64 * 3), 3);
  positions.setUsage(THREE.DynamicDrawUsage);
  const geometry = new THREE.PlaneGeometry(1, 1);
  geometry.setAttribute('burstPos', positions);
  const params = { color: 0xffffff, size: 0.3, opacity: 1, blending };
  let material;
  if (shard) {
    const rotations = new THREE.InstancedBufferAttribute(new Float32Array(64 * 2), 2);
    geometry.setAttribute('burstRot', rotations);
    material = createShardMaterial(positions, rotations, params);
  } else material = createParticleMaterial(positions, params);
  const mesh = new THREE.Mesh(geometry, material);
  mesh.count = 1;
  return mesh;
}

/** The same program with the depth test off: on WebGPU that is its own render pipeline. */
function noDepth(mesh) {
  mesh.material.depthTest = false;
  return mesh;
}

/**
 * Pool key → factory. Every key a shape module can produce has a row here.
 * A factory may return several meshes: they compile in ONE job (the queue
 * is capped at 32 jobs), which is how a material-state VARIANT rides along
 * with its base — the impact stars, the chain bolt and the aura ticks draw
 * with `depthTest = false` (they must show through the body they sit on),
 * and a pipeline for that state is compiled on first use unless warmed here.
 */
const POOLED = {
  burstRing: () => burstRing(0xffffff).mesh,
  slashArc: () => slashArc(3, 45, 0xffffff).mesh,
  'starFlash:normal': () => [starFlash(0xffffff).mesh, noDepth(starFlash(0xffffff).mesh)],
  'starFlash:additive': () => starFlash(0xffffff, { additive: true }).mesh,
  hexRing: () => hexRing(0xffffff).mesh,
  lineWall: () => lineWall(5, 1, 0xffffff).mesh,
  zoneRing: () => zoneRing(0xffffff).mesh,
  boltBeam: () => [boltBeam(0xffffff).mesh, noDepth(boltBeam(0xffffff).mesh)],
  muzzleFlash: () => [muzzleFlash(0xffffff).mesh, noDepth(muzzleFlash(0xffffff).mesh)],
  smear: () => smear(0xffffff).mesh,
  shell: () => shell(0xffffff).mesh,
  cracks: () => cracks(0xffffff).mesh,
  column: () => column(0xffffff).mesh,
  vortex: () => vortex(0xffffff).mesh,
  runeTrap: () => runeTrap(0xffffff).mesh,
  nullDome: () => nullDome(0xffffff).mesh,
  nullFloor: () => nullFloor(0xffffff).mesh,
  hexShield: () => hexShield(0xffffff).mesh,
  iceCrust: () => iceCrust(0x9ad8ff).mesh,
  hazeRing: () => hazeRing(0xffffff).mesh,
  teleDisc: () => teleDisc(0xffffff).mesh,
  teleCone: () => teleCone(0xffffff, 0.7).mesh,
  teleLine: () => teleLine(0xffffff).mesh,
  teleRim: () => teleRim(0xffffff).mesh,
  teleReticle: () => teleReticle(0xffffff).mesh,
};

/**
 * How many instances of each pooled key the warmup PRE-FILLS the pool with.
 * Warming one instance and keeping it compiled the program, but left the
 * pool's free list empty: the first cast still built a fresh material (the
 * node-graph cache key alone is 5–20 ms), and a second live instance — two
 * Deindexed zones, six stun stars, three chain bolts — built another. These
 * are the counts a busy fight reaches at once; every one of them is acquired,
 * compiled and RELEASED to the pool, so the first casts find them ready.
 */
const DEPTH = {
  burstRing: 3, slashArc: 2, 'starFlash:normal': 4, 'starFlash:additive': 2, hexRing: 3, lineWall: 2, zoneRing: 3,
  boltBeam: 3, muzzleFlash: 3, smear: 4, shell: 2, cracks: 2,
  column: 4, vortex: 2, runeTrap: 3, nullDome: 2, nullFloor: 2,
  hexShield: 3, iceCrust: 3, hazeRing: 4,
  teleDisc: 3, teleCone: 2, teleLine: 2, teleRim: 2, teleReticle: 2,
};

/** Non-pooled graphs that a first use would otherwise compile mid-fight. */
const OWN = {
  aimIndicator: () => buildIndicatorMesh(),
  aimTick: () => buildTickMesh(),
  dashGhost: () => buildDashGhost(),
  'particle:normal': () => particle(false, 'normal'),
  'particle:additive': () => particle(false, 'additive'),
  'shard:normal': () => particle(true, 'normal'),
  'shard:additive': () => particle(true, 'additive'),
};

/** What the warmup covers: the pooled keys (by name) and the private graphs. */
export function prewarmCoverage() {
  return { pooled: Object.keys(POOLED), own: Object.keys(OWN) };
}

/** Every pooled key a shape module declares it can produce. */
export function declaredPoolKeys() {
  return [
    'burstRing', 'slashArc', 'starFlash:normal', 'starFlash:additive', 'hexRing', 'lineWall', 'zoneRing', 'boltBeam', 'muzzleFlash', 'smear', 'shell', 'cracks',
    ...ZONE_SHAPE_KEYS,
    ...STATUS_SHAPE_KEYS,
    ...TELEGRAPH_SHAPE_KEYS,
  ];
}

/**
 * The scene sweep: every culled object is drawn ONCE whether or not the
 * camera sees it — on the frame it is found in the scene — so the renderer
 * builds its render object (the node-graph cache key, the pipeline, the
 * outline pass's material) when it arrives, behind the boot overlay for an
 * arena, instead of the first time the camera happens to turn onto it.
 *
 * Found with the build log in docs/design/vfx3/tools/shoot.mjs: an arena's
 * floor tiles, candles and collision fallbacks are built lazily as they
 * enter the frustum, 2–20 ms each, so a dash, a Parallel Path, a Hover Slam —
 * anything that moves the camera — landed 50–65 ms frames, and so did a
 * plain walk. Neither the abilities nor their VFX were the cost. Per object
 * rather than per world because an arena's GLB parts and the bodies arrive
 * over the frames AFTER the world is announced.
 *
 * Objects the kit already draws unculled (every pooled shape, the particle
 * systems) are never touched. The scan runs every SCAN_EVERY frames and
 * costs one traversal.
 */
const SCAN_EVERY = 3;

export function createSceneSweep(scene) {
  const restore = [];
  let frame = 0;
  let swept = 0;
  let forceAll = false;
  return {
    /** A world was (re)built: sweep everything again, marks or not. */
    arm() {
      forceAll = true;
    },
    /** Call once per frame BEFORE the render. Culling is off for newly
     *  found objects on this frame and put back exactly as it was next frame. */
    beforeRender() {
      if (restore.length) {
        for (const o of restore) o.frustumCulled = true;
        restore.length = 0;
      }
      frame++;
      if (!forceAll && frame % SCAN_EVERY !== 0) return 0;
      const all = forceAll;
      forceAll = false;
      let n = 0;
      scene.traverse((o) => {
        // Only what the renderer builds a render object for.
        if (!(o.isMesh || o.isSprite || o.isLine || o.isPoints)) return;
        if (!o.frustumCulled || (o.userData.sweptOnce && !all)) return;
        o.userData.sweptOnce = true;
        o.frustumCulled = false;
        restore.push(o);
        n++;
      });
      if (n > 0) swept += n;
      return n;
    },
    stats: () => ({ swept, restoring: restore.length }),
  };
}

/**
 * Where a warmed mesh is drawn for its one live frame: far under the deck,
 * culling off, so the draw is issued (the render object exists) and no
 * fragment is ever shaded.
 */
const LIVE_DRAW_Y = -60;

/** Two animation frames from now — at least one full render in between. */
function twoFrames() {
  return new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
}

export function createShaderWarmup(renderer, scene, camera, { canRun, schedule, nextFrames } = {}) {
  // Compiling in an isolated scene builds the PROGRAM; it does not build the
  // render object the live scene and camera will use (its node cache key,
  // its bindings, its pipeline lookup — 5–8 ms per mesh on this deck, named
  // by the build log under Deindexed's warm run and the chain's cold one,
  // where six or ten warmed shapes first-drew in ONE frame). So after the
  // compile every warmed mesh is drawn once in the live scene, off-screen,
  // before it is released to the pool or retained.
  const frames = nextFrames ?? (typeof requestAnimationFrame === 'function' ? twoFrames : null);
  // Non-pooled graphs keep material/pipeline ownership for the renderer
  // lifetime: disposing a warmed material immediately can release the cached
  // pipeline and undo the warmup. Pooled shapes are instead RELEASED to their
  // pool once compiled, so the fight's first acquires find warm instances.
  // Never rendered from here.
  const retained = [];
  let pooled = 0;
  const isolated = new THREE.Scene();
  isolated.fog = scene.fog;
  isolated.environment = scene.environment;
  const factories = [
    ...Object.entries(POOLED).map(([key, create]) => ({ create, depth: DEPTH[key] ?? 1, pooledKey: key })),
    ...Object.values(OWN).map((create) => ({ create, depth: 1, pooledKey: null })),
  ];
  let maxSyncMs = 0;
  let startedAt = null;
  let completed = 0;
  let liveDrawn = 0;
  const jobs = factories.map(({ create, depth, pooledKey }) => async () => {
    const start = performance.now();
    startedAt ??= start;
    const meshes = [];
    for (let i = 0; i < depth; i++) {
      const made = create();
      for (const mesh of Array.isArray(made) ? made : [made]) meshes.push(mesh);
    }
    for (const mesh of meshes) {
      mesh.frustumCulled = false;
      isolated.add(mesh);
    }
    try {
      // The optional third argument supplies target lighting/background while
      // only the isolated scene's objects are projected for compilation.
      const compiling = renderer.compileAsync(isolated, camera, scene);
      maxSyncMs = Math.max(maxSyncMs, performance.now() - start);
      await compiling;
      for (const mesh of meshes) isolated.remove(mesh);
      if (frames) {
        for (const mesh of meshes) {
          mesh.position.set(0, LIVE_DRAW_Y, 0);
          mesh.scale.setScalar(0.001);
          scene.add(mesh);
        }
        await frames();
        liveDrawn += meshes.length;
      }
      completed++;
    } finally {
      for (const mesh of meshes) {
        mesh.removeFromParent();
        if (pooledKey && releaseCelShape(mesh)) pooled++;
        else retained.push(mesh);
      }
    }
  });
  const queue = new DeferredWarmup({ jobs, canRun, schedule: schedule ?? (callback => {
    if (typeof window.requestIdleCallback === 'function') window.requestIdleCallback(callback);
    else setTimeout(callback, 0);
  }) });
  return {
    afterFrame: options => queue.afterFrame(options),
    stats: () => ({ ...queue.stats(), retained: retained.length, pooled, liveDrawn, completed, total: factories.length, maxSyncMs, startedAt }),
  };
}
